import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Check, ChevronDown, X, Cpu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../ui/dropdown-menu';

const providerColors = {
  openai: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  anthropic: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  google: { bg: 'bg-sky-500/10', text: 'text-sky-400', border: 'border-sky-500/30' },
};

export default function ModelSelector({ 
  label, 
  models, 
  selected, 
  onSelect, 
  multiple,
  type 
}) {
  const isSelected = (provider, modelName) => {
    return selected.some(s => s.provider === provider && s.model_name === modelName);
  };

  const handleSelect = (provider, modelName) => {
    onSelect({ provider, model_name: modelName });
  };

  const removeModel = (e, provider, modelName) => {
    e.stopPropagation();
    onSelect({ provider, model_name: modelName });
  };

  return (
    <div className="space-y-2" data-testid={`model-selector-${type}`}>
      <Label className="text-xs text-[#94A3B8] flex items-center gap-1.5">
        <Cpu className="w-3 h-3" />
        {label}
      </Label>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "w-full min-h-[38px] px-3 py-2 text-left text-sm rounded-sm border transition-colors",
              "bg-[#030712] border-[#1E293B] hover:border-[#334155]",
              "flex items-center justify-between gap-2"
            )}
            data-testid={`${type}-model-trigger`}
          >
            <div className="flex flex-wrap gap-1 flex-1">
              {selected.length === 0 ? (
                <span className="text-[#64748B]">Select model...</span>
              ) : (
                selected.map((m, i) => (
                  <Badge
                    key={`${m.provider}-${m.model_name}-${i}`}
                    className={cn(
                      "text-[10px] font-mono flex items-center gap-1",
                      providerColors[m.provider]?.bg,
                      providerColors[m.provider]?.text,
                      providerColors[m.provider]?.border
                    )}
                  >
                    {m.model_name}
                    {multiple && (
                      <X 
                        className="w-3 h-3 cursor-pointer hover:opacity-70" 
                        onClick={(e) => removeModel(e, m.provider, m.model_name)}
                      />
                    )}
                  </Badge>
                ))
              )}
            </div>
            <ChevronDown className="w-4 h-4 text-[#64748B] flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          className="w-56 bg-[#0F172A] border-[#1E293B]" 
          align="start"
        >
          {Object.entries(models).map(([provider, providerModels]) => (
            <div key={provider}>
              <DropdownMenuLabel className="text-[10px] uppercase text-[#64748B] font-medium">
                {provider}
              </DropdownMenuLabel>
              {providerModels.map((model) => (
                <DropdownMenuItem
                  key={`${provider}-${model.name}`}
                  onClick={() => handleSelect(provider, model.name)}
                  className={cn(
                    "text-sm cursor-pointer",
                    isSelected(provider, model.name) 
                      ? "bg-[#0EA5E9]/10 text-[#F1F5F9]" 
                      : "text-[#94A3B8]"
                  )}
                  data-testid={`model-option-${model.name}`}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-mono text-xs">{model.display}</span>
                    {isSelected(provider, model.name) && (
                      <Check className="w-4 h-4 text-[#0EA5E9]" />
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-[#1E293B]" />
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
